import * as CL from '@design-system/component-library';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables';
import {
  alvZeroMsg,
  catalogCorporateMessageLabelMsg,
  companysShareOfFeeMsg,
  contractPeriodMsg,
  damageInsuranceMsg,
  defaultBillingAccountMsg,
  inUseMsg,
  monthMsg,
  notInUseMsg,
  productTypeMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import {
  getCatalogStatus,
  getCorporateShareAsText,
  getDamageInsuranceDisplayableItems,
  productTypeDetails,
} from '../../common/utils/catalogUtils.js';
import { formatTimeStampToHHmm, formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { Catalog } from '../../generated/api/catalog.js';
import { ModificationStatus } from '../CatalogList/ModificationStatus.js';
import { BillingAccountSelection } from './CatalogPartials.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { BillingAccountHeader } from '../../generated/api/billingAccountHeader';
import type { VirtualCatalog } from '../../generated/api/virtualCatalog.js';
import type { VirtualCatalogStatus } from '../../generated/api/virtualCatalogStatus.js';

interface VirtualCatalogInformationForStatusProps {
  catalog: Catalog;
  modificationStatus?: VirtualCatalogStatus;
  billingAccount?: string;
  selectBillingAccount?: {
    billingAccounts: BillingAccountHeader[];
    virtualCatalog: VirtualCatalog;
  };
  mdmId: string;
}

export const CatalogInformationForStatus = (props: VirtualCatalogInformationForStatusProps) => {
  const { catalog, modificationStatus, billingAccount, selectBillingAccount, mdmId } = props;
  const navigate = useNavigate();
  const items: CLDescriptionItem[] = [];

  // Status
  if (modificationStatus) {
    items.push({
      title: t.ASQT(statusMsg),
      description: <ModificationStatus status={modificationStatus} noSubtext />,
    });
  }

  // Last Modified
  items.push({
    title: t.PM1H('Last modified'),
    description: catalog.lastModified
      ? `${formatTimestampToDDMMYYYY(catalog.lastModified)} ${formatTimeStampToHHmm(catalog.lastModified)}`
      : '-',
  });

  // Default billing account
  items.push({
    title: t.EYPA(defaultBillingAccountMsg),
    description: selectBillingAccount ? (
      <BillingAccountSelection
        billingAccounts={selectBillingAccount.billingAccounts}
        currentBillingAccountId={catalog.billingAccountId}
        onUpdateBillingAccount={() => {
          navigate(`${paths.COMPANY_INFO_CATALOG_LIST_EDIT}?mdmId=${mdmId}`, {
            state: { virtualCatalog: selectBillingAccount.virtualCatalog, mdmId: mdmId },
          });
        }}
      />
    ) : (
      billingAccount
    ),
  });

  // Corporate share
  if (catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING) {
    items.push({
      title: `${t.TH83(companysShareOfFeeMsg)} (${t.S8TX(alvZeroMsg)})`,
      description: getCorporateShareAsText(catalog.corporateShare),
    });
  }

  // Product type
  items.push({
    title: t.R6HP(productTypeMsg),
    description: catalog.productType && productTypeDetails()[catalog.productType].displayValue,
  });

  // Contract period
  items.push({
    title: t.ULI0(contractPeriodMsg),
    description: catalog.contractPeriod ? `${catalog.contractPeriod} ${t.XXVX(monthMsg)}` : '-',
  });

  // Damage insurance
  items.push({
    title: t.QSXP(damageInsuranceMsg),
    description:
      catalog.damageInsurance && catalog.damageInsurance.length > 0
        ? getDamageInsuranceDisplayableItems(catalog.damageInsurance)
        : t.MUF5(notInUseMsg),
  });

  // Device enrollment program
  items.push({
    title: t.Q03S('Device enrollment program'),
    description: catalog.enrollmentProgramConsent
      ? `${t.V34H(inUseMsg)}, alias: ${catalog.enrollmentProgramAlias || '-'}`
      : t.MUF5(notInUseMsg),
  });

  // Is order acceptor required to select BA?
  items.push({
    title: t.RXBO('Billing account is selected separately for each order'),
    description: catalog.approverMustSelectBillingAccount ? t.V34H(inUseMsg) : t.MUF5(notInUseMsg),
  });

  // Is restricted list of billing accounts used?
  items.push({
    title: t.RP0S('Restricted list of billing accounts'),
    description:
      catalog.selectedBillingAccountIds && catalog.selectedBillingAccountIds.length > 0
        ? t.V34H(inUseMsg)
        : t.MUF5(notInUseMsg),
  });

  return (
    <div className="of-virtual-catalog-configuration">
      <CL.Description items={items} />

      <CL.Description
        className={dsClass.MARGIN_TOP_5}
        columns={1}
        items={[
          {
            title: t.C8DA(catalogCorporateMessageLabelMsg),
            description: catalog.corporateMessage || t.MUF5(notInUseMsg),
          },
        ]}
      />
    </div>
  );
};

interface CatalogInformationProps {
  billingAccounts: BillingAccountHeader[];
  catalog: Catalog;
  virtualCatalog: VirtualCatalog;
  mdmId: string;
}

export const CatalogInformation = (props: CatalogInformationProps) => {
  const { catalog, virtualCatalog, billingAccounts, mdmId } = props;
  return (
    <CatalogInformationForStatus
      catalog={catalog}
      modificationStatus={getCatalogStatus(virtualCatalog)}
      selectBillingAccount={{ virtualCatalog, billingAccounts }}
      mdmId={mdmId}
    />
  );
};
